import { PostRepository } from "@amityco/ts-sdk";
import {
  TextareaHTMLAttributes,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";

import { NOTIFICATION_CONTENT_TYPE, NOTIFICATION_TYPE } from "@/api";
import { Button } from "@/components";
import { Image02 } from "@/icons";
import {
  useAmityAuth,
  useCommunityGroups,
  useTranslate,
} from "@/providers/context";
import { cn } from "@/utils";

import { AttachmentPreview } from "../";
import {
  ACCEPTED_IMAGE_FILE_TYPES,
  ACCEPTED_VIDEO_FILE_TYPES,
  MAX_ATTACHMENTS,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_LABEL,
} from "../../const";
import { useCommunityError } from "../../hooks/useCommunityError";
import { useSendCommunityNotification } from "../../hooks/useSendCommunityNotification";
import { PostItem } from "../../types";
import { isCommunityAdmin, uploadMedia } from "../../util";

// Currently only allowing one attachment per post
// Note: If we want to allow multiple attachments in future
// we need to prevent mix of photos and videos as amity doesn't support it
interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  className?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, ...props }: TextAreaProps, ref) => (
    <textarea
      ref={ref}
      className={cn(
        "p2-regular text-item-primary transition-colors duration-100 placeholder:text-item-secondary",
        "outline-none disabled:bg-transparent",
        className,
      )}
      {...props}
    />
  ),
);

TextArea.displayName = "TextArea";

const useAutoResize = (value: string | undefined) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = "93px";
    textarea.style.height = `${Math.max(93, textarea.scrollHeight)}px`;
  }, [value]);

  return textareaRef;
};

export const CommunityPostInput = ({
  post,
  onSuccess,
  onCancel,
}: {
  post?: PostItem; // Provided if editing an existing post
  onSuccess: () => void;
  onCancel: () => void;
}) => {
  const { translate } = useTranslate();
  const { activeGroup } = useCommunityGroups();
  const {
    handlePostingError,
    textContainsBlockedContent,
    showBlockedContentToast,
  } = useCommunityError();
  const { authState } = useAmityAuth();
  const sendNotification = useSendCommunityNotification();

  const [value, setValue] = useState<string | undefined>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [pendingAttachment, setPendingAttachment] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<
    Amity.File<"image" | "video">[]
  >([]);

  const isAdmin = isCommunityAdmin(authState?.authenticatedUser);

  const textareaRef = useAutoResize(value);

  const isEditing = !!post;
  const isLoading = useMemo(
    () => isSubmitting || !!pendingAttachment || !!uploadProgress,
    [isSubmitting, pendingAttachment, uploadProgress],
  );

  const saveIsDisabled = useMemo(() => {
    if (isLoading || !value) {
      return true;
    }

    if (isEditing) {
      const textEdited = post?.textContent !== value;
      const postAttachmentIds = attachments.map((a) => a.fileId);
      const attachmentIds = post?.attachments?.map((a) => a.fileId);
      const attachmentsEdited =
        JSON.stringify(postAttachmentIds) !== JSON.stringify(attachmentIds);

      if (!textEdited && !attachmentsEdited) {
        return true;
      }
    }

    return false;
  }, [
    isLoading,
    value,
    isEditing,
    attachments,
    post?.attachments,
    post?.textContent,
  ]);

  useEffect(() => {
    if (post) {
      setValue(post.textContent);
      setAttachments(post.attachments ?? []);
    }
  }, [post]);

  const handleAddAttachment = useCallback(
    async (file: File) => {
      if (
        ![...ACCEPTED_IMAGE_FILE_TYPES, ...ACCEPTED_VIDEO_FILE_TYPES].includes(
          file.type,
        )
      ) {
        toast(
          translate("Invalid file type. Please select an image or video file."),
        );
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast(
          `${translate("File size must be less than")} ${MAX_FILE_SIZE_LABEL}`,
        );
        return;
      }

      setUploadProgress(0);
      setPendingAttachment(file);

      try {
        const uploadedFile = await uploadMedia(file, setUploadProgress);
        setAttachments([...attachments, uploadedFile]);
      } catch (error) {
        toast(translate("File upload failed. Please try again."));
      } finally {
        setPendingAttachment(null);
        setUploadProgress(null);
      }
    },
    [
      attachments,
      setAttachments,
      setUploadProgress,
      setPendingAttachment,
      translate,
    ],
  );

  const handleSubmitPost = useCallback(async () => {
    setIsSubmitting(true);

    if (!value || !activeGroup?.CommunityID) {
      setIsSubmitting(false);
      return;
    }

    if (textContainsBlockedContent(value)) {
      showBlockedContentToast();
      setIsSubmitting(false);
      return;
    }

    try {
      if (isEditing) {
        await PostRepository.editPost(post.id, {
          data: {
            text: value,
          },
          attachments,
        }).catch(handlePostingError);
      } else {
        const { data: newPost } = await PostRepository.createPost({
          data: {
            text: value,
          },
          targetType: "community",
          targetId: activeGroup.CommunityID,
          attachments,
        }).catch(handlePostingError);

        if (newPost) {
          sendNotification.mutate({
            notification_type: isAdmin
              ? NOTIFICATION_TYPE.postByAdmins
              : NOTIFICATION_TYPE.newPosts,
            content_type: NOTIFICATION_CONTENT_TYPE.post,
            content_id: newPost.postId,
            community_id: activeGroup.CommunityID,
          });
        }
      }

      setValue("");
      onSuccess();
    } catch (e) {
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    post,
    value,
    activeGroup?.CommunityID,
    attachments,
    isEditing,
    sendNotification,
    isAdmin,
    setValue,
    setIsSubmitting,
    onSuccess,
    handlePostingError,
    textContainsBlockedContent,
    showBlockedContentToast,
  ]);

  return (
    <div className="flex w-full flex-col">
      <TextArea
        ref={textareaRef}
        className="max-h-[calc(85vh-302px)] min-h-[93px] w-full resize-none"
        onChange={(e) => {
          setValue(e.target.value);
        }}
        value={value}
        placeholder={translate("What's on your mind?")}
        disabled={isSubmitting}
      />
      <div className="mt-8 flex h-[100px] w-fit max-w-full gap-2 overflow-x-auto">
        {attachments.map((file) => (
          <AttachmentPreview
            key={file.fileId}
            file={file}
            onRemove={() =>
              setAttachments(
                attachments.filter(({ fileId }) => fileId !== file.fileId),
              )
            }
          />
        ))}
        {pendingAttachment && (
          <AttachmentPreview
            pending={{
              progress: uploadProgress ?? 0,
              file: pendingAttachment,
            }}
          />
        )}
      </div>
      <div className="flex w-full items-center justify-between pb-6 pt-4">
        <Button
          size="s"
          variant="tertiary"
          icon={<Image02 />}
          className="h-fit rounded-md p-[2px]"
          disabled={isLoading || attachments.length >= MAX_ATTACHMENTS}
          onClick={() => {
            const fileInput = document.createElement("input");
            fileInput.type = "file";
            fileInput.accept = [
              ...ACCEPTED_IMAGE_FILE_TYPES,
              ...ACCEPTED_VIDEO_FILE_TYPES,
            ].join(", ");
            fileInput.onchange = (event) => {
              const file = (event.target as HTMLInputElement).files?.[0];
              if (file) {
                handleAddAttachment(file);
              }
            };
            fileInput.click();
          }}
        />
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            {translate("Cancel")}
          </Button>
          <Button
            disabled={saveIsDisabled}
            loading={isSubmitting}
            onClick={handleSubmitPost}
          >
            {translate(isEditing ? "Save" : "Post")}
          </Button>
        </div>
      </div>
    </div>
  );
};
